/**
 * EAV.RDP.ANALYTICS, Type FinancialContracts: the IPA request LSEG's Excel add-in builds
 * (Refinitiv for Office 26.30.2), ported from its QPS pipeline:
 *
 *   QPSPayloadProcessor.preProcessPayload   short getFields formula, `v<n>`, `async`, Options
 *   QPSPayloadProcessor.getModifiedPayload  dates padded, Type/SubType spaces removed, then
 *     QpsContract.create                    lower case, `#n` args, Type/SubType validated
 *                                           (any error here is prefixed `Validation error: `)
 *   QPSService.preProcess                   processor map, Universe separator check, then
 *     <SubType>Processor.getRequestInput    Universe × UniverseParams → IPA's body
 *     UniversalConverter.process            Excel date serials in date-typed keys → ISO dates
 *
 * The body is then cut into batches of at most 10 instruments [B]. What LSEG does after the
 * body (the request, the async endpoint) is not ported here.
 *
 * Rulings: P1, whitespace in Type/SubType is accepted (LSEG's removeSpaces runs before
 * QpsContract.validateTypesWhiteSpaces, which therefore never fires); P2, Types other than
 * financialcontracts and help, and the voyageservice subtype, answer `notAvailable` once LSEG's
 * own validation has passed. Help builds no request (`HelpRequest`): LSEG answers it locally.
 *
 * [B] LSEG does not split: from RDPSyncInstrumentLimits (10, fx 50) instruments on, its
 *     AnalyticsExecutor sends the whole universe to `async-financial-contracts`. EAV only has
 *     the sync route, so it sends batches of 10 (plan, Review Focus 1).
 */

import { FC_CONFIGS, FC_SUBTYPES, FcConfig, FcSubType } from "./config.generated";
import { CURVES_DATE_PARAMS, DAC_DATE_PARAMS, FC_DATE_PARAMS, FC_OPTION_BASE_DATE_PARAMS } from "./dates.generated";
import { FC_OUTPUT_FIELDS } from "./fields.generated";
import {
  AnalyticsError,
  UniverseCtx,
  UniverseItem,
  checkInstrumentCodeWithParams,
  getUniverseArg,
  parseUniverse,
  parseUniverseParams,
  validateUniverseSeparator,
} from "./universe";

/** IPA's financial-contracts body. */
export type AnalyticsBody = {
  universe: UniverseItem[];
  fields: string[];
  outputs: string[];
  pricingParameters?: Record<string, unknown>;
  marketData?: Record<string, unknown>;
};

export type AnalyticsRequest = {
  /** LSEG's subtype, lower case (`fxforward`), as the response processors are keyed. */
  subType: string;
  batches: AnalyticsBody[];
  /** What LSEG's response processor lays out: Output, else Universe. */
  output: string;
  /** Options after LSEG's clean-up (`async`, `v<n>` and unknown options removed). */
  options: string;
  getFields: boolean;
};

/** Type Help: no request, LSEG answers it from its own table (response.ts). */
export type HelpRequest = {
  help: "about";
  /** LSEG's Output (getFields lists the headers). */
  output: string;
  /** Options after LSEG's clean-up; "" takes the help processor's own default. */
  options: string;
};

/** Type DatesAndCalendars: the dates-and-calendars service's body, as LSEG posts it. */
export type DatesRequest = {
  /** LSEG's subtype: addperiods, countperiods, holidays, isworkingday, dateschedule (beta) or dateschedule.1. */
  dates: string;
  /** The hub's name for the subtype's endpoint (dates-add-periods…). */
  service: string;
  /** One request per UniverseParams group; date-schedule takes its one request as an object. */
  body: Record<string, unknown>[] | Record<string, unknown>;
  /** Output (it only picks the columns: it is not sent) and the cleaned Options. */
  output: string;
  options: string;
  /** UniverseParams as typed: the layout reads holidayOutputs from it. */
  universeParams: string;
  /**
   * Output omitted: how LSEG's processor sees it when it splits Output, `undefined` when nothing
   * follows it (LSEG's argument list is shorter), else `null` (at_dacu_add_noout, _noout_chfd).
   * [A] EAV never sees Dest's value: an omitted Output and Options with a Dest reads as `undefined`.
   */
  outputMissing?: "null" | "undefined";
};

/** Type Curves: the curves service's body, as LSEG posts it. */
export type CurvesRequest = {
  /** LSEG's subtype: zcdefinitions, zccurves, zcconstituents, irtriangulatedefinitions, ccdefinitions… */
  curves: string;
  /** The hub's name for the subtype's endpoint (curves-zc-curves…). */
  service: string;
  body: { universe: UniverseItem[]; outputs?: string[] };
  /** Output and Options as the response processor reads them (getFields/metadata rewrites them). */
  output: string;
  options: string;
};

/** Type VolatilitySurface: the surfaces service's body, as LSEG posts it. */
export type SurfacesRequest = {
  /** LSEG's subtype: eti, fx, ir, cap, swaption or hw1f. */
  surfaces: string;
  /** The hub's name for the endpoint: every subtype posts to `surfaces`. */
  service: string;
  body: { universe: UniverseItem[]; outputs?: string[] };
};

export type AnalyticsRequestResult =
  | { error: string }
  | { notAvailable: string }
  | HelpRequest
  | DatesRequest
  | CurvesRequest
  | SurfacesRequest
  | AnalyticsRequest;

export const ANALYTICS_BATCH_SIZE = 10;

const SEP = ";"; // LSEG: ProcessorsConfig.defaultSeparator (27821)
const FC = "financialcontracts";
const VOYAGE = "voyageservice";
const HELP = "help";
const DAC = "datesandcalendars";
const CURVES = "curves";
const VSF = "volatilitysurface";

// LSEG: QpsContract.argIndexMap (97731)
const ARG = { analyticsType: 0, subType: 1, universe: 2, universeParams: 3, output: 4, options: 5, destinationCell: 6 };
const ARG_COUNT = 7;
const ALLOWED_OPTIONS = ["rh", "ch", "in", "env", "async", "transpose", "showinvalidfield", "streaming"]; // LSEG: QpsContract.allowedOptions (97731)

// LSEG: AnalyticsType, SubType (495)
const ANALYTICS_TYPES = [FC, "datesandcalendars", "curves", "volatilitysurface", HELP];
const SUB_TYPES = [
  ...FC_SUBTYPES, "asyncfinancialcontracts", "addperiods", "holidays", "dateschedule", "countperiods",
  "isworkingday", "dateschedule.1", "zcdefinitions", "zccurves", "zcconstituents", "irtriangulatedefinitions",
  "ccdefinitions", "cctriangulatedefinitions", "cccurves", "forwardcurves", "surfaces", "asyncsurfaces", "eti",
  "fx", "ir", "cap", "swaption", "hw1f", "about",
];

/**
 * The subtypes each Type has a processor for. LSEG's FC map also holds asyncfinancialcontracts
 * with no processor (`new null` there); it is left out, so it gets the map text.
 */
// LSEG: qpsProcessorsMap (71747), qpsProcessorsMapFC (23244), DAC (56263), CS (22269), VSF (55136)
const PROCESSOR_SUBTYPES: Record<string, readonly string[]> = {
  [FC]: FC_SUBTYPES,
  datesandcalendars: ["addperiods", "countperiods", "holidays", "isworkingday", "dateschedule", "dateschedule.1"],
  curves: [
    "zcdefinitions", "zccurves", "zcconstituents", "irtriangulatedefinitions", "ccdefinitions",
    "cctriangulatedefinitions", "cccurves", "forwardcurves",
  ],
  volatilitysurface: ["eti", "fx", "ir", "cap", "swaption", "hw1f", "asyncsurfaces"],
  [HELP]: ["about"],
};

// LSEG's texts (en-US, 47243)
const INVALID_TYPE = "#N/A Invalid value for type";
const INVALID_SUBTYPE = "#N/A Invalid value for subtype";
const NO_PROCESSOR_MAP = "The QPS Processor map could not be created with the provided analytics type and subtype.";
const notAvailable = (what: string) => ({ notAvailable: `EAV: RDP.Analytics type ${what} is not available yet` });

// LSEG: DisplayOptionVariants (78755), as QPSPayloadProcessor.validateAndFinalizeOptions joins them
const ID_FD = ["rh:id", "ch:fd", "rh:id ch:fd", "rh:id ch=fd", "rh=id ch:fd", "ch:fd rh:id", "ch:fd rh=id", "ch=fd rh:id", "rh=id", "ch=fd", "rh=id ch=fd", "ch=fd rh=id"];
const FD_ID = ["rh:fd", "ch:id", "rh:fd ch:id", "rh:fd ch=id", "rh=fd ch:id", "ch:id rh:fd", "ch:id rh=fd", "ch=id rh:fd", "rh=fd", "ch=id", "rh=fd ch=id", "ch=id rh=fd"];
const DT_ID = ["rh:dt", "ch:id", "rh:dt ch:id", "rh:dt ch=id", "rh=dt ch:id", "ch:id rh:dt", "ch:id rh=dt", "ch=id rh:dt", "rh=dt", "ch=id", "rh=dt ch=id", "ch=id rh=dt"];
const ID_DT = ["rh:id", "ch:dt", "rh:id ch:dt", "rh:id ch=dt", "rh=id ch:dt", "ch:dt rh:id", "ch:dt rh=id", "ch=dt rh:id", "rh=id", "ch=dt", "rh=id ch=dt", "ch=dt rh=id"];
const DISPLAY_VARIANTS = [
  ...ID_FD, ...FD_ID, "rh:id;fd", "rh:fd;id", "rh=id;fd", "rh=fd;id", "ch:id;fd", "ch:fd;id", "ch=id;fd", "ch=fd;id", ...DT_ID, ...ID_DT,
];

const OUTPUT_FIELD = /(\w+)(\[(\d+)\]|\.(\w+)|(\[[\d-.]+\]))/; // LSEG: BaseFCResProcessorConfig.regexPatterns.outputField (7376)
const FX_SUBTYPES = ["fxcross", "fxspot", "fxforward", "fxnondeliverableforward", "fxswap", "fxtimeoptionforward", "multileg", "fxforwardforward"];

// LSEG: toCamelCase module (44181)
const capitalize = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : "");
const removeLineBreaks = (s: string | null | undefined) => (s ? s.replace(/(\r\n|\n|\r)/gm, "") : "");
const lowerCaseAndNoWhitespace = (s: string | null | undefined) => (s ? s.toLowerCase().replace(/\s+/g, "") : "");
const text = (v: unknown): string | null => (v == null ? null : typeof v === "string" ? v : String(v));

/**
 * The IPA request for `RDP.Analytics(Type, SubType, Universe, UniverseParams, Output, Options,
 * Dest, Arg1…Arg20)`, or LSEG's error text, or EAV's not-available text.
 */
export function buildAnalyticsRequest(rawArgs: unknown[]): AnalyticsRequestResult {
  try {
    // Excel passes an omitted argument as null; LSEG's getArgs would turn undefined into "".
    let args: unknown[] = rawArgs.map((a) => (a === undefined ? null : a));
    preProcessPayload(args);
    args = getModifiedPayload(args);
    return preProcess(args);
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

// ---------------------------------------------------------------------------------------------
// QPSPayloadProcessor.preProcessPayload (93209), with QpsContract's static helpers (97731)

/** Rewrites Universe…Options and SubType in place. */
function preProcessPayload(args: unknown[]): void {
  const isShort = checkShortFormula(args, "getFields", /^metadata(:.+)?$/i);
  checkSubTypeVersion(args);
  checkAndReplaceAsyncOption(args);
  if (!isShort && args[ARG.subType] === "dateschedule") args[ARG.subType] = "dateschedule.1";
  if (args[ARG.options]) args[ARG.options] = validateAndFinalizeOptions(String(args[ARG.options]));
}

const matches = (value: string, criterion: string | RegExp) =>
  typeof criterion === "string" ? criterion.toLowerCase() === value.toLowerCase() : criterion.test(value);

/** `RDP.Analytics(T, S, "getFields")`: Universe and UniverseParams shift right by two. */
// LSEG: QpsContract.checkShortFormula (97731)
function checkShortFormula(args: unknown[], ...criteria: (string | RegExp)[]): boolean {
  let isShort = false;
  for (const c of criteria) {
    if (matches(String(args[ARG.universe] ?? "").trim(), c)) args.splice(ARG.universe, 0, "", "");
    if (!isShort) isShort = matches(String(args[ARG.output] ?? "").trim(), c);
  }
  return isShort;
}

/** Option `v2` makes SubType `bond.2` (which then fails validation: an2_opt_v2). */
// LSEG: QpsContract.checkSubTypeVersion (97731)
function checkSubTypeVersion(args: unknown[]): void {
  if (!args[ARG.options]) return;
  const opts = ` ${String(args[ARG.options])} `;
  const v = / v(\d+) /i.exec(opts);
  if (v) args[ARG.subType] = `${String(args[ARG.subType] ?? "").replace(/[.]\d+$/, "")}.${v[1]}`;
  args[ARG.options] = opts.replace(/ v\d+ /gi, " ").trim().replace(/\s{2,}/g, " ");
}

/** `async` is removed from Options; LSEG then posts to its async endpoint, EAV ignores it. */
// LSEG: QpsContract.checkAndReplaceAsyncOption (97731)
function checkAndReplaceAsyncOption(args: unknown[]): void {
  if (!args[ARG.options]) return;
  let opts = String(args[ARG.options]);
  if (/async/i.test(opts)) opts = opts.replace(/async/gi, "");
  args[ARG.options] = opts.trim().replace(/\s+/g, " ");
}

// LSEG: QPSPayloadProcessor.validateAndFinalizeOptions (93209)
function validateAndFinalizeOptions(options: string): string {
  return options
    .split(" ")
    .map((opt) => {
      const display = opt.match(/^[crf].[:=][crf]./gim);
      if (display && !DISPLAY_VARIANTS.includes(display[0].toLowerCase()))
        throw new AnalyticsError("Invalid display options entered: " + opt);
      return ALLOWED_OPTIONS.includes(opt.split(/[:=]/)[0].toLowerCase()) ? opt : "";
    })
    .join(" ")
    .trim()
    .replace("  ", " ");
}

// ---------------------------------------------------------------------------------------------
// QPSPayloadProcessor.getModifiedPayload (93209) and QpsContract.create (97731)

function getModifiedPayload(input: unknown[]): unknown[] {
  let args = input.map((a) => (typeof a === "string" ? fixDates(a) : a)); // LSEG: QpsContract.fixDatesInPayloadArgs
  try {
    args[ARG.analyticsType] = removeSpaces(args[ARG.analyticsType]);
    args[ARG.subType] = removeSpaces(args[ARG.subType]);
    args = getArgs(args);
    args = formatRawInput(args);
    validate(args[ARG.analyticsType] as string, args[ARG.subType] as string);
  } catch (e) {
    throw new AnalyticsError("Validation error: " + (e instanceof Error ? e.message : String(e)));
  }
  return args;
}

const removeSpaces = (v: unknown) => (v ? `${v}`.replace(/\s/g, "") : "");

/**
 * Dates after `Key:` padded to `yyyy-mm-dd`. LSEG's dateWithTimeList is empty (QPSServiceConfig,
 * 60730), so a `T…Z` time after such a date is dropped; a date with no key is left as is.
 */
// LSEG: fixDates, validateModifiedDate, getFieldName (93456)
function fixDates(s: string): string {
  const re = /\b(\w+.:)?([12]\d{3}-\d{1,2}-\d{1,2})(T\d{1,2}:\d{1,2}:\d{1,2}Z)?\b/gi;
  for (let m = re.exec(s); m != null; m = re.exec(s)) {
    const field = m[1] && m[2] ? m[1].split(":")[0] : "";
    let replacement = m[0];
    if (field) {
      const [y, mo = "", d = ""] = m[2].split("-");
      replacement = `${field}:${y}-${mo.length === 1 ? "0" + mo : mo}-${d.length === 1 ? "0" + d : d}`;
    }
    s = s.replace(m[0], replacement);
  }
  return s;
}

/** The first seven arguments as text: a range joins its non-empty cells with `;`, a number reads as "". */
// LSEG: InputContract.getArgs, handleInputParameters, prepareFinalElems, convert3DTo1DArray (68509)
function getArgs(args: unknown[]): unknown[] {
  const head = args.slice(0, ARG_COUNT).map(inputParameter);
  const rest = args.slice(ARG_COUNT).map((a) => (Array.isArray(a) ? inputParameter(a) : a));
  if (String(head[ARG.universe] ?? "").trim().toLowerCase() === "metadata") {
    head[ARG.universe] = "";
    head[ARG.output] = "metadata";
  }
  return [...head, ...rest];
}

function inputParameter(a: unknown): unknown {
  if (typeof a === "string" || (typeof a === "object" && !Array.isArray(a))) return a; // null stays null
  if (!Array.isArray(a)) return "";
  const rows: string[] = [];
  for (const row of a) {
    const cells = (Array.isArray(row) ? row : [row]).filter((c) => c !== "" && c !== "null" && c !== null);
    if (cells.length) rows.push(cells.join(SEP));
  }
  return rows.join(SEP);
}

/** Type and SubType lower case without whitespace; `#n` in the next four replaced by Arg n. */
// LSEG: QpsContract.formatRawInput, replaceHashtagValues (97731)
function formatRawInput(args: unknown[]): unknown[] {
  const head: unknown[] = [
    lowerCaseAndNoWhitespace(args[0] as string),
    lowerCaseAndNoWhitespace(args[1] as string),
    ...args.slice(2, ARG_COUNT - 1),
  ].map((v) => {
    const refs = typeof v === "string" ? v.match(/#\d+/g) : null;
    if (!refs) return v;
    return refs.reduce((s, ref) => {
      const value = args[ARG_COUNT - 1 + Number(ref.slice(1))];
      return value ? s.replace(ref, value as string) : s;
    }, v as string);
  });
  return [...head, args[ARG.destinationCell], ...args.slice(ARG_COUNT)];
}

/** Only each field's last rule speaks in LSEG's Validator, so `required` never shows. */
// LSEG: QpsContract.validate (97731), Validator.validate / validateField (63925)
function validate(analyticsType: string, subType: string): void {
  const errors: string[] = [];
  if (!ANALYTICS_TYPES.includes(String(analyticsType).toLowerCase())) errors.push(INVALID_TYPE);
  if (!SUB_TYPES.includes(String(subType).toLowerCase())) errors.push(INVALID_SUBTYPE);
  if (errors.length) throw new Error(errors.join(" "));
}

// ---------------------------------------------------------------------------------------------
// QPSService.preProcess (36844)

function preProcess(args: unknown[]): AnalyticsRequestResult {
  const analyticsType = args[ARG.analyticsType] as string;
  const subType = args[ARG.subType] as string;
  if (!PROCESSOR_SUBTYPES[analyticsType].includes(subType)) throw new AnalyticsError(NO_PROCESSOR_MAP); // LSEG: getProcessorMap
  validateUniverseSeparator(text(args[ARG.universe])); // LSEG: QPSUtils.validateSeparator (62066)
  if (analyticsType === HELP) return helpRequest(args);
  if (analyticsType === DAC) return datesRequest(args);
  if (analyticsType === CURVES) return curvesRequest(args);
  if (analyticsType === VSF) return surfacesRequest(args);
  if (analyticsType !== FC) return notAvailable(analyticsType);

  const body = makeProcessor(subType as FcSubType).getRequestInput(args);
  if (subType === VOYAGE) return notAvailable(VOYAGE);
  // LSEG: QPSService.checkIfIsMetadataRequest: Output or Universe `metadata…` lists IPA's parameters
  const output = text(args[ARG.output]);
  const universe = text(args[ARG.universe]);
  if (/^metadata/i.test(output ?? "") || /^metadata/i.test(universe ?? "")) return notAvailable("metadata");

  let converted: AnalyticsBody;
  try {
    converted = convertDates(body, dateTypeParameters(subType, body.universe)); // LSEG: universalConverter.process
  } catch (e) {
    // LSEG hands the message to ErrorHandler.getErrorResponse(message, type), which returns the
    // converter's texts (excelDateTooBig, a RangeError from toISOString) unchanged.
    return { error: e instanceof Error ? e.message : String(e) };
  }
  converted.fields = ipaFieldNames(converted.fields, outputFieldNames(subType, converted.universe));

  // Each batch is its own copy: the instruments' shared branches (Merger) and the params included.
  const batches: AnalyticsBody[] = [];
  for (let i = 0; i === 0 || i < converted.universe.length; i += ANALYTICS_BATCH_SIZE) {
    const batch: AnalyticsBody = {
      universe: converted.universe.slice(i, i + ANALYTICS_BATCH_SIZE),
      fields: converted.fields,
      outputs: converted.outputs,
    };
    if (converted.pricingParameters) batch.pricingParameters = converted.pricingParameters;
    if (converted.marketData) batch.marketData = converted.marketData;
    batches.push(JSON.parse(JSON.stringify(batch)) as AnalyticsBody);
  }
  return {
    subType,
    batches,
    output: output || universe || "", // LSEG: BaseFinancialContractsResponseProcessor (24686)
    options: text(args[ARG.options]) ?? "",
    getFields: isGetFields(output) || isGetFields(universe),
  };
}

const isGetFields = (v: string | null) => v != null && v.trim().toLowerCase() === "getfields";

/**
 * Help/About: LSEG's AboutProcessor builds no request (BaseProcessor.getRequestInput is `{}`) and
 * has no endpoint (AnalyticsExecutor answers null), and no metadata: a metadata request throws.
 */
// LSEG: QPSService.preProcess, checkIfIsMetadataRequest, execute (36844); qpsProcessorsMap (71747)
function helpRequest(args: unknown[]): HelpRequest {
  const output = text(args[ARG.output]) ?? "";
  if (/^metadata/i.test(output) || /^metadata/i.test(text(args[ARG.universe]) ?? "")) {
    throw new AnalyticsError(`No metadata for ${HELP}:${args[ARG.subType]}`);
  }
  return { help: "about", output, options: text(args[ARG.options]) ?? "" };
}

// ---------------------------------------------------------------------------------------------
// Type DatesAndCalendars: BaseDateAndCalendarProcessor (88903) and its subtypes

// LSEG: RDPEndpointUrls (57380) for qpsProcessorsMapDAC (56263), as the hub names them
const DAC_SERVICES: Record<string, string> = {
  addperiods: "dates-add-periods",
  countperiods: "dates-count-periods",
  holidays: "dates-holidays",
  isworkingday: "dates-is-working-day",
  "dateschedule.1": "dates-date-schedule",
  dateschedule: "dates-date-schedule",
};

// LSEG: dateScheduleProcessorConfig (11808), the beta DateSchedule's sections (30174, v1's, has none)
const BETA_SCHEDULE_SECTIONS: Record<string, string[]> = {
  rollConventionDefinition: ["dayOfWeek", "CalendarDayOfMonth", "weekofMonth", "monthOfYear"],
  dateAdjustment: ["adjustmentPeriod", "adjustmentDirection", "dateMovingConvention", "endOfMonthConvention", "calendars"],
};
const sectionOf = (sections: Record<string, string[]>, key: string): string | null => {
  const k = key.toLowerCase().trim();
  for (const [section, names] of Object.entries(sections)) if (names.some((n) => n.toLowerCase().trim() === k)) return section;
  return null;
};
const HOLIDAY_OUTPUTS = ["date", "name", "names", "calendars", "countries"]; // LSEG: possibleValuesHolidayOutputs (68487)
export const isHolidayOutput = (s: string | undefined) => !!s && HOLIDAY_OUTPUTS.includes(s);

// LSEG's texts (en-US, 47243; 30174)
const DAC_UNIVERSE_NOT_EMPTY = "Dates And Calendars universe should be empty!";
const HOLIDAY_OUTPUTS_FORMAT = "holidayOutputs must be a string array";
const INVALID_PERIOD_TYPE = "The period must be of type string.";
const invalidPeriod = (value: unknown) => `The period value (${value}) is not valid.`;
const MULTIPLE_SCHEDULES = "Multiple requests for DateSchedule is not supported yet.";

type Obj = Record<string, unknown>;

/**
 * The service's body: one request per UniverseParams group, each the global params merged with
 * its group, keys where typed (no sections), then each subtype's own clean-up.
 */
// LSEG: QPSService.preProcess (36844) with BaseDateAndCalendarProcessor's validationFields and
// getDACRequests (88903), AddPeriodsProcessor (61116), GetHolidaysProcessor (11310), DateScheduleProcessor (55248)
function datesRequest(args: unknown[]): DatesRequest | { error: string } | { notAvailable: string } {
  const subType = args[ARG.subType] as string;
  const rawUniverse = text(args[ARG.universe]);
  const universe = rawUniverse ?? "";
  const output = text(args[ARG.output]) ?? "";
  const metadata = /^metadata/i.test(output) || /^metadata/i.test(universe);
  // A metadata request filters the universe rule out (QPSService.filterValidationFields). The rule
  // is `universe.match(/^$/)`: an omitted Universe (null) throws, as LSEG shows (at_dac_add_plain).
  if (!metadata) {
    if (rawUniverse === null) throw new TypeError("Cannot read properties of null (reading 'match')");
    if (!/^$/.test(universe)) throw new AnalyticsError(DAC_UNIVERSE_NOT_EMPTY);
  }
  // `DateSchedule` as typed is the beta processor (59550): only a lower-case `dateschedule` (or
  // option v1) becomes dateschedule.1 (QPSPayloadProcessor.preProcessPayload compares before lower-casing).
  const sections = subType === "dateschedule" ? BETA_SCHEDULE_SECTIONS : {};
  const ctx: UniverseCtx = {
    subType,
    instrumentCodeField: "",
    paramType: (k) => sectionOf(sections, k),
    isRootKey: (k) => Object.keys(sections).includes(k),
    defaultSection: null,
    checkParam: (key, value) => {
      if (key.toLowerCase() === "period") validatePeriodValue(value);
    },
  };
  const { global, perInstrument } = parseUniverseParams(removeLineBreaks(text(args[ARG.universeParams])), ctx);
  const requests: Obj[] = [];
  if (!perInstrument.length) requests.push(holidaysOutput(global, subType, args));
  for (let i = 0; perInstrument[i]; i++) requests.push(holidaysOutput(merge(global, perInstrument[i]) as Obj, subType, args));

  let body: Obj[] | Obj = requests;
  if (subType === "addperiods") requests.forEach((r) => (delete r.endMovingConvention, delete r.id));
  if (subType === "dateschedule.1" || subType === "dateschedule") {
    if (requests.length > 1) throw new Error(MULTIPLE_SCHEDULES);
    body = subType === "dateschedule" ? convertV1ToBeta(requests[0]) : convertBetaToV1(requests[0]);
  }
  if (metadata) return notAvailable("metadata");

  try {
    body = formatDates(body as Obj, DAC_DATE_PARAMS[subType]) as Obj[] | Obj; // LSEG: universalConverter.process
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
  const request: DatesRequest = {
    dates: subType,
    service: DAC_SERVICES[subType],
    body,
    output,
    options: text(args[ARG.options]) ?? "",
    universeParams: text(args[ARG.universeParams]) ?? "",
  };
  if (args[ARG.output] == null) request.outputMissing = args.slice(ARG.output + 1).every((a) => a == null) ? "undefined" : "null";
  return request;
}

// LSEG: BaseDateAndCalendarProcessor.validatePeriodValue (88903)
function validatePeriodValue(value: unknown): void {
  if (typeof value !== "string") throw new Error(INVALID_PERIOD_TYPE);
  if (!/^(\d+[DWMY]+){1,3}$/i.test(value)) throw new Error(invalidPeriod(value));
}

/**
 * The request's holidayOutputs. Holidays take them from Output (Names always, Tag never); the
 * other subtypes from `holidayOutputs:[…]` in UniverseParams (else in Options, which LSEG has
 * already cleaned of it), Names added.
 */
// LSEG: BaseDateAndCalendarProcessor.getHolidaysOutput (88903), GetHolidaysProcessor.getHolidaysOutput (11310)
function holidaysOutput(e: Obj, subType: string, args: unknown[]): Obj {
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  if (subType === "holidays") {
    const output = text(args[ARG.output]);
    if (output) {
      const names = output.split(SEP).map((s) => s.toLowerCase());
      const tag = names.indexOf("tag");
      if (tag !== -1) names.splice(tag, 1);
      const kept = names.filter(isHolidayOutput);
      const name = kept.indexOf("name");
      if (name !== -1) kept.splice(name, 1);
      kept.push("names");
      e.holidayOutputs = kept.map(cap);
    }
    return e;
  }
  const params = text(args[ARG.universeParams]) || "";
  const source = params.indexOf("holidayOutputs") !== -1 ? params : text(args[ARG.options]) ?? "";
  const m = source.match(/holidayOutputs[:=](\[(.*)\])*/i);
  if (m) {
    if (!m[2]) throw new Error(HOLIDAY_OUTPUTS_FORMAT);
    const list = m[2]
      .split(SEP)
      .map((s) => s.toLowerCase())
      .filter((s) => isHolidayOutput(s) && s !== "name")
      .map(cap);
    if (!list.includes("Names")) list.push("Names");
    e.holidayOutputs = list;
  }
  return e;
}

/** startDate and endDate as the beta DateSchedule names them. */
// LSEG: DateScheduleProcessor.convertV1ToBeta (59550)
function convertV1ToBeta(e: Obj): Obj {
  if (e.startDate) (e.start = e.startDate), delete e.startDate;
  if (e.endDate) (e.end = e.endDate), delete e.endDate;
  return e;
}

/** The beta keys DateSchedule accepts, moved to the v1 service's, Daily by default. */
// LSEG: DateScheduleProcessor.convertBetaToV1 (55248)
function convertBetaToV1(e: Obj): Obj {
  const adjustment = e.dateAdjustment as Obj | undefined;
  const roll = e.rollConventionDefinition as Obj | undefined;
  if (e.start) e.startDate = e.start;
  if (e.end) e.endDate = e.end;
  if (adjustment && adjustment.calendars) e.calendars = adjustment.calendars;
  if (roll) {
    if (roll.calendarDayOfMonth) e.calendarDayOfMonth = roll.calendarDayOfMonth;
    if (roll.dayOfWeek) e.dayOfWeek = roll.dayOfWeek;
  }
  if (!e.frequency) e.frequency = "Daily";
  for (const k of [
    "start", "end", "adjustmentDirection", "adjustmentPeriod", "dateFormat", "dateMovingConvention", "endOfMonthConvention",
    "monthOfYear", "rollConventionType", "weekOfMonth", "rollConventionDefinition", "dateAdjustment", "endMovingConvention",
  ])
    delete e[k];
  return e;
}

// ---------------------------------------------------------------------------------------------
// Type Curves: BaseCSProcessor (97446) and its subtypes

// LSEG: RDPEndpointUrls (57380) for qpsProcessorsMapCS (22269), as the hub names them
const CURVES_SERVICES: Record<string, string> = {
  zcdefinitions: "curves-zc-curve-definitions",
  zccurves: "curves-zc-curves",
  zcconstituents: "curves-zc-curves",
  irtriangulatedefinitions: "curves-ir-triangulate-definitions",
  ccdefinitions: "curves-cc-definitions",
  cctriangulatedefinitions: "curves-cc-triangulate-definitions",
  cccurves: "curves-cc-curves",
  forwardcurves: "curves-forward-curves",
};

// LSEG: ZCProcessorConfig (47036), ForwardCurvesProcessorConfig (25271), CCurrencyProcessorConfig (42720);
// the definitions subtypes have no config (every key stays where it is typed)
const ZC_CURVE_DEFINITION = [
  "id", "currency", "mainConstituentAssetClass", "riskType", "indexName", "marketDataLocation", "source", "name",
  "discountingTenor", "firstHistoricalAvailabilityDate", "availableTenors", "cutOffs", "definitionExpiryDate",
  "crossCurrencyDefinitions", "curveTenors", "ignoreExistingDefinition", "indexTenors", "isNonDeliverable",
  "pivotCurveDefinition", "referenceCurveDefinition",
];
const ZC_CURVE_PARAMETERS = [
  "valuationDate", "priceSide", "interestCalculationMethod", "extrapolationMode", "interpolationMode", "referenceTenor",
  "turns", "useSteps", "steps", "useConvexityAdjustment", "convexityAdjustment", "useMultiDimensionalSolver",
  "calendarAdjustment", "calendars", "compoundingType", "marketDataAccessDeniedFallback", "ignoreExistingDefinition",
];
const CONSTITUENTS = ["crossCurrencyInstruments", "interestRateInstruments"];
const ZC_SECTIONS = { curveDefinition: ZC_CURVE_DEFINITION, curveParameters: ZC_CURVE_PARAMETERS };
const CURVES_SECTIONS: Record<string, Record<string, string[]>> = {
  zccurves: ZC_SECTIONS,
  zcconstituents: ZC_SECTIONS,
  forwardcurves: {
    curveDefinition: ZC_CURVE_DEFINITION,
    curveParameters: [...ZC_CURVE_PARAMETERS, "pivotCurveParameters", "referenceCurveParameters", "valuationDateTime"],
    constituents: CONSTITUENTS,
  },
  cccurves: {
    curveDefinition: [
      "source", "baseCurrency", "baseIndexName", "quotedCurrency", "quotedIndexName", "pivotCurrency", "pivotIndexName",
      "curveTenors", "mainConstituentAssetClass", "crossCurrencyDefinitions", "interestRateCurveDefinitions", "isNonDeliverable",
    ],
    curveParameters: ["valuationDate", "interpolationMode", "extrapolationMode", "turnAdjustment", "ignorePivotCurrencyHolidays", "turnAdjustments"],
    constituents: CONSTITUENTS,
  },
};

/**
 * The curves service's body: `{universe}`, each item the global params merged with its group and
 * its Universe entry, keys in their section (else where typed), no instrumentType. A bare code
 * gives nothing, except for zcdefinitions (`{currency: code}`). getFields and metadata blank
 * Universe and UniverseParams and put `ch:fd` in Options, as LSEG does.
 */
// LSEG: QPSService.preProcess (36844) with BaseCSProcessor (97446) over BaseFCProcessor.getUniverse
// (41170), ZeroCouponDefinitionsProcessor (69062), ZeroCouponProcessor (93322), CrossCurrencyProcessor (57502)
function curvesRequest(args: unknown[]): CurvesRequest | { error: string } | { notAvailable: string } {
  const subType = args[ARG.subType] as string;
  const metadata = /^metadata/i.test(text(args[ARG.output]) ?? "") || /^metadata/i.test(text(args[ARG.universe]) ?? "");
  checkSpecialInputContract(args, "getFields", /^metadata(:.+)?$/i);
  const sections = CURVES_SECTIONS[subType] ?? {};
  const universe = () =>
    curvesUniverse(args, {
      subType,
      instrumentCodeField: "",
      paramType: (k) => sectionOf(sections, k),
      isRootKey: (k) => Object.keys(sections).includes(k),
      defaultSection: null,
      codeItem: (code) => (subType === "zcdefinitions" ? { currency: code } : {}),
      tagInstrumentType: false,
    });
  let body: CurvesRequest["body"];
  const items = searchAndModifyObjectKeys(universe(), "crossCurrencyInstruments", upperKeys) as UniverseItem[];
  // ZeroCouponProcessor reads the universe again, without the upper-cased keys
  if (subType === "zccurves" || subType === "zcconstituents") body = { universe: universe(), outputs: ["Constituents", "DetailedCurvePoint"] };
  else if (subType === "zcdefinitions" && (args[ARG.output] ?? "") === "getfields") body = { universe: [] };
  else body = { universe: items };
  if (metadata) return notAvailable("metadata");
  try {
    body = { ...body, universe: body.universe.map((item) => formatDates(item, CURVES_DATE_PARAMS[subType])) };
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
  return {
    curves: subType,
    service: CURVES_SERVICES[subType],
    body,
    output: text(args[ARG.output]) ?? "",
    options: text(args[ARG.options]) ?? "",
  };
}

/** The universe items: Universe's entries over UniverseParams' groups over its global params. */
// LSEG: BaseFCProcessor.getUniverse (41170); BaseCSProcessor keeps no pricingParameters/marketData
function curvesUniverse(args: unknown[], ctx: UniverseCtx): UniverseItem[] {
  const universeArg = getUniverseArg(text(args[ARG.universe]));
  checkInstrumentCodeWithParams(universeArg);
  const items = parseUniverse(universeArg, ctx) || [];
  const { global, perInstrument } = parseUniverseParams(removeLineBreaks(text(args[ARG.universeParams])) || "", ctx);
  delete global.pricingParameters;
  delete global.marketData;
  const out: UniverseItem[] = [];
  for (let i = 0; items[i] || perInstrument[i]; i++) out.push(merge(merge(global, perInstrument[i] || {}), items[i] || {}) as UniverseItem);
  return out;
}

/**
 * getFields or metadata in Output (or Universe): Universe and UniverseParams blanked, Output set
 * (from Universe when it held it, UniverseParams then becoming Options), and `ch:fd` put first in
 * Options when it names no `ch`.
 */
// LSEG: BaseFCProcessor.checkSpecialInputContract (41170)
function checkSpecialInputContract(args: unknown[], ...criteria: (string | RegExp)[]): void {
  const universe = (text(args[ARG.universe]) ?? "").trim();
  const params = (text(args[ARG.universeParams]) ?? "").trim();
  let output = (text(args[ARG.output]) ?? "").trim();
  let options = (text(args[ARG.options]) ?? "").trim();
  for (const c of criteria) {
    const inOutput = typeof c === "string" ? output.toLowerCase() === c.toLowerCase() : c.test(output);
    const inUniverse = typeof c === "string" ? universe.toLowerCase() === c.toLowerCase() : c.test(universe);
    if (inUniverse) (output = universe), (options = params);
    if (!hasCH(options)) options = "ch:fd" + (options ? ` ${options}` : "");
    if (inOutput || inUniverse) {
      args[ARG.universe] = "";
      args[ARG.universeParams] = "";
      args[ARG.output] = output;
      args[ARG.options] = options;
    }
  }
}

/** Is there a `ch:`/`ch=` option? */
// LSEG: BaseFCProcessor.hasCH (41170)
function hasCH(options: string): boolean {
  return options.split(/\s+/).some((t) => {
    const at = t.search(/ch/i);
    return at !== -1 && [":", "="].includes(t[at + 2]) && at === 0;
  });
}

// ---------------------------------------------------------------------------------------------
// Type VolatilitySurface: VolatilitySurfaceProcessor (91784) and its subtypes

// LSEG: EtiVSFProcessorConfig (61314), FxProcessorConfig (32979), InterestRateVSFConfig (60872)
const SURFACE_LAYOUT = ["format", "dataPoints", "xValues", "yValues", "xPointCount", "yPointCount"];
const RATES_SURFACE_SECTIONS = {
  underlyingType: ["Cap", "Swaption", "Hw1fCalibration"],
  underlyingDefinition: [
    "exchange", "instrumentCode", "discountingType", "strikePercent", "exerciseDates", "calibrationInstrumentEndDate",
    "calibrationInstrument", "calibrationInstrumentStructure", "swapStyle", "underlyingSwapStructure", "indexTenor",
    "referenceCapletTenor", "indexName",
  ],
  surfaceParameters: [
    "xAxis", "yAxis", "includeCapletsVolatility", "volatilityAdjustmentType", "inputVolatilityType", "outputVolatilityType",
    "calculationDate", "meanReversionPercent", "useDualCalibration", "zAxis", "beta", "shiftPercent",
  ],
  surfaceLayout: SURFACE_LAYOUT,
};
const SURFACE_SECTIONS: Record<string, Record<string, string[]>> = {
  eti: {
    underlyingDefinition: ["exchange", "instrumentCode", "referenceCapletTenor", "discountingType"],
    surfaceParameters: [
      "inputVolatilityType", "volatilityModel", "calculationDate", "filters", "xAxis", "yAxis", "priceSide", "timeStamp",
      "moneynessType", "sviAlphaExtrapolation", "weights",
    ],
    surfaceLayout: SURFACE_LAYOUT,
  },
  fx: {
    underlyingDefinition: ["fxCrossCode", "isInvertedPair", "source"],
    surfaceParameters: [
      "volatilityModel", "fxSwapCalculationMethod", "calculationDate", "xAxis", "yAxis", "priceSide", "deltaConvention", "returnAtm",
    ],
    surfaceLayout: SURFACE_LAYOUT,
  },
  ir: RATES_SURFACE_SECTIONS,
  cap: RATES_SURFACE_SECTIONS,
  swaption: RATES_SURFACE_SECTIONS,
  hw1f: RATES_SURFACE_SECTIONS,
};
const RATES_SURFACES = ["ir", "cap", "swaption", "hw1f"];

/**
 * The date-typed names of each subtype's local metadata (VolatilitySurface is not in module 3438,
 * so MetadataParser.parse gives null and LSEG takes the processor's own): EtiVSF (29977), Fx
 * (25371) CalculationDate, Rates (62963) CalibrationInstrumentEndDate, all "DateTime"
 * (ExerciseDates is "DateTime[]", not a date type, but a number list under a date key converts anyway).
 */
const SURFACE_DATE_PARAMS: Record<string, Record<string, string>> = {
  eti: { calculationdate: "datetime" },
  fx: { calculationdate: "datetime" },
  ir: { calibrationinstrumentenddate: "datetime" },
  cap: { calibrationinstrumentenddate: "datetime" },
  swaption: { calibrationinstrumentenddate: "datetime" },
  hw1f: { calibrationinstrumentenddate: "datetime" },
};

/**
 * The surfaces service's body: `{universe, outputs?}`. Each item is tagged `underlyingType:
 * Capitalize(subtype)` (Ir and Hw1f too, which the service refuses: at_vs_ir), a bare code is its
 * underlying (fx: fxCrossCode), `dataPoints` values are sent as text. Output is sent, capitalised
 * and unique; the rates subtypes ask for Headers and Data when it is empty.
 */
// LSEG: QPSService.preProcess (36844) with VolatilitySurfaceProcessor (91784) over BaseFCProcessor.getUniverse
// (41170), InterestRateVSFProcessor (49238); UniverseVisitorImpl.visitUniverseParam (80759)
function surfacesRequest(args: unknown[]): SurfacesRequest | { error: string } | { notAvailable: string } {
  const subType = args[ARG.subType] as string;
  // LSEG's map holds asyncsurfaces with no processor (`new null`): its text is not known [A]
  if (!SURFACE_SECTIONS[subType]) return notAvailable(`${VSF} ${subType}`);
  const metadata = /^metadata/i.test(text(args[ARG.output]) ?? "") || /^metadata/i.test(text(args[ARG.universe]) ?? "");
  checkSpecialInputContract(args, /^metadata(:.+)?$/i);
  const sections = SURFACE_SECTIONS[subType];
  const universe = curvesUniverse(args, {
    subType,
    instrumentCodeField: "",
    paramType: (k) => sectionOf(sections, k),
    isRootKey: (k) => Object.keys(sections).includes(k),
    defaultSection: null,
    codeItem: (code) => ({ underlyingDefinition: { [subType === "fx" ? "fxCrossCode" : "instrumentCode"]: code } }),
    typeTag: { key: "underlyingType", value: capitalize(subType) },
    paramValue: (k, v) => (k.toLowerCase() === "datapoints" && Array.isArray(v) ? v.map(stringValues) : v),
  });
  const body: SurfacesRequest["body"] = { universe };
  const output = (text(args[ARG.output]) ?? "").trim();
  if (output) body.outputs = Array.from(new Set(output.split(SEP).map(capitalize)));
  if (RATES_SURFACES.includes(subType) && !body.outputs) body.outputs = ["Headers", "Data"];
  if (metadata) return notAvailable("metadata");
  try {
    body.universe = body.universe.map((item) => formatDates(item, SURFACE_DATE_PARAMS[subType]));
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
  return { surfaces: subType, service: "surfaces", body };
}

/** Each value of each object as text: `{x: 10}` → `{x: "10"}`. */
// LSEG: VolatilitySurfaceProcessor.fixDataPoints (91784)
const stringValues = (o: unknown): Obj => {
  const out: Obj = {};
  for (const [k, v] of Object.entries(o as Obj)) out[k] = `${v}`;
  return out;
};

const upperKeys = (o: unknown): unknown => {
  if (!o || typeof o !== "object" || Array.isArray(o)) return o;
  const out: Obj = {};
  for (const [k, v] of Object.entries(o)) out[k.toUpperCase()] = v;
  return out;
};

/** A copy where every value under `key` (any case, any depth) goes through `modify`. */
// LSEG: searchAndModifyObjectKeys, modifyObjectKeys (24130)
function searchAndModifyObjectKeys(o: unknown, key: string, modify: (v: unknown) => unknown): unknown {
  if (!o || typeof o !== "object") return o;
  const out: Obj | unknown[] = Array.isArray(o) ? [] : {};
  for (const [k, v] of Object.entries(o)) {
    const value = searchAndModifyObjectKeys(v, key, modify);
    (out as Obj)[k] = k.toLowerCase() === key.toLowerCase() ? modify(value) : value;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// The UniversalConverter: Excel date serials in date-typed keys become ISO dates

/**
 * LSEG's dateTypeParameters for the request: QPSService.getOptionType (the first instrument's
 * underlyingType, for option) and MetadataParser.parse (79380), reduced to the date names
 * generated in dates.generated.ts. A subtype without metadata (multileg, fxforwardforward) gets
 * null, and LSEG's converter then leaves the body alone.
 */
function dateTypeParameters(subType: string, universe: UniverseItem[]): Record<string, string> | null {
  const category = (name: string) => (Object.prototype.hasOwnProperty.call(FC_DATE_PARAMS, name) ? FC_DATE_PARAMS[name] : null);
  const own = category(subType);
  if (!own) return null;
  const optionType = subType === "option" && universe[0] ? universe[0].instrumentDefinition?.underlyingType : "";
  if (optionType) return { ...FC_OPTION_BASE_DATE_PARAMS, ...(category(String(optionType).toLowerCase()) || {}) };
  return own;
}

/**
 * The output field names LSEG's metadata knows for the subtype, lower case -> IPA's spelling:
 * MetadataParser.getFields (79380) on the subtype and, for option, the first instrument's
 * underlyingType (QPSService.getOptionType), its list winning. Generated in fields.generated.ts.
 */
function outputFieldNames(subType: string, universe: UniverseItem[]): Map<string, string> {
  const list = (name: string) => (Object.prototype.hasOwnProperty.call(FC_OUTPUT_FIELDS, name) ? FC_OUTPUT_FIELDS[name] : []);
  const optionType = subType === "option" && universe[0] ? universe[0].instrumentDefinition?.underlyingType : "";
  const names = [...list(subType), ...(optionType ? list(String(optionType).toLowerCase()) : [])];
  return new Map(names.map((n) => [n.toLowerCase(), n]));
}

/**
 * Each requested field in IPA's spelling where the metadata has it, else as typed: IPA's field
 * names are case-sensitive (`Cleanprice` comes back InvalidField), LSEG's lookup is not. A name
 * that becomes a repeat (`CleanPrice;cleanprice`) is sent once: the layout reads both columns
 * from the one header, as LSEG shows them (both 89.831).
 */
// LSEG: QPSService.processHeadersOnly (36844), keyed by name.toLowerCase()
function ipaFieldNames(fields: string[], names: Map<string, string>): string[] {
  return Array.from(new Set(fields.map((f) => names.get(f.toLowerCase()) ?? f)));
}

/** The universe items and the top-level pricingParameters (not marketData), converted in place. */
// LSEG: UniversalConverter.process (48123), BaseConverter.convert / transformUniverses (64973)
function convertDates(body: AnalyticsBody, dates: Record<string, string> | null): AnalyticsBody {
  if (!dates) return body;
  const out: AnalyticsBody = { ...body, universe: body.universe.map((item) => formatDates(item, dates)) };
  if (body.pricingParameters) out.pricingParameters = formatDates(body.pricingParameters, dates);
  return out;
}

const isDateKey = (k: string) => ["end", "start"].includes(k) || k.includes("date");

// LSEG: BaseConverter.loopObjAndFormatDate (64973)
function formatDates<T extends Record<string, unknown>>(obj: T, dates: Record<string, string>): T {
  const o = obj as Record<string, unknown>;
  for (const key in o) {
    const k = key.toLowerCase();
    const v = o[key];
    if (!v) continue;
    if (Array.isArray(v)) o[key] = finalItems(v, k, dates);
    else if ((v as object).constructor === Object) o[key] = formatDates(v as Record<string, unknown>, dates);
    else if (Object.prototype.hasOwnProperty.call(dates, k) && !isNaN(v as number) && isDateKey(k))
      o[key] = dates[k] === "date" ? excelIsoDate(v).split("T")[0] : excelIsoDate(v);
  }
  return obj;
}

/** In a list under a date key every number is converted (to a date-time), whatever the metadata. */
// LSEG: BaseConverter.getFinalItems (64973)
function finalItems(items: unknown[], key: string, dates: Record<string, string>): unknown[] {
  const out: unknown[] = [];
  items.forEach((e) => {
    if ((e as object).constructor === Object) out.push(formatDates(e as Record<string, unknown>, dates));
    else if (!isNaN(e as number) && isDateKey(key)) out.push(excelIsoDate(e));
    else out.push(e);
  });
  return out;
}

const EXCEL_START_MS = -22089888e5; // 1900-01-01T00:00:00Z
const DAY_MS = 864e5;

/**
 * An Excel serial as `yyyy-mm-ddTHH:MM:SSZ`: day 1 is 1900-01-01, and from 61 on one day is
 * taken off for Excel's 29 February 1900. Values coerce as in JS (`"46318"` works).
 */
// LSEG: ExcelDateConverter.getIsoDate (30920); text excelDateTooBig (47243)
function excelIsoDate(serial: unknown): string {
  const e = serial as number;
  let ms: number;
  if (e < 61) ms = EXCEL_START_MS + DAY_MS * (e - 1);
  else {
    if (e >= 2958466) throw new Error("Input dates and times are too large");
    ms = EXCEL_START_MS + DAY_MS * (e - 2);
  }
  return `${new Date(ms).toISOString().split(".")[0]}Z`;
}

// ---------------------------------------------------------------------------------------------
// The processors

/** LSEG: BaseFCProcessor (41170) over BaseProcessor (27957). */
class BaseFCProcessor {
  protected readonly config: FcConfig;
  protected args: unknown[] = [];

  constructor(
    protected readonly subType: FcSubType,
    protected readonly instrumentCodeField = "instrumentCode",
    protected readonly instrumentCodeByDefault = true,
  ) {
    this.config = FC_CONFIGS[subType];
  }

  getRequestInput(args: unknown[]): AnalyticsBody {
    this.args = args;
    if (isGetFields(text(args[ARG.output])) || isGetFields(text(args[ARG.universe])))
      return { universe: this.getFieldsUniverse(), fields: [], outputs: ["Headers"] };
    const { universe, globalFCRequestParams } = this.getUniverse();
    const body: AnalyticsBody = { universe, fields: this.getFields(), outputs: ["Headers", "Data"] };
    if (Object.keys(globalFCRequestParams.pricingParameters).length > 0) body.pricingParameters = globalFCRequestParams.pricingParameters;
    if (Object.keys(globalFCRequestParams.marketData).length > 0) body.marketData = globalFCRequestParams.marketData;
    return body;
  }

  /** LSEG's getParamType: the paramTypes section holding the key, case-insensitive and trimmed. */
  protected getParamType(key: string): string | null {
    const k = key.toLowerCase().trim();
    for (const [section, names] of Object.entries(this.config.paramTypes))
      if (names.some((n) => n.toLowerCase().trim() === k)) return section;
    return null;
  }

  /** LSEG's isRootKey: case-sensitive, on the camel-cased key. */
  protected isRootKey(key: string): boolean {
    return Object.keys(this.config.paramTypes).includes(key);
  }

  protected getUniverse(): { universe: UniverseItem[]; globalFCRequestParams: { pricingParameters: Record<string, unknown>; marketData: Record<string, unknown> } } {
    const universeArg = getUniverseArg(text(this.args[ARG.universe]));
    const paramsArg = removeLineBreaks(text(this.args[ARG.universeParams])) || "";
    checkInstrumentCodeWithParams(universeArg);
    const ctx: UniverseCtx = {
      subType: this.args[ARG.subType] as string,
      instrumentCodeField: this.instrumentCodeField,
      paramType: (k) => this.getParamType(k),
      isRootKey: (k) => this.isRootKey(k),
    };
    const items = parseUniverse(universeArg, ctx) || [];
    const { global, perInstrument } = parseUniverseParams(paramsArg, ctx);
    const globalFCRequestParams = {
      pricingParameters: (global.pricingParameters || {}) as Record<string, unknown>,
      marketData: (global.marketData || {}) as Record<string, unknown>,
    };
    delete global.pricingParameters;
    delete global.marketData;
    const universe: UniverseItem[] = [];
    for (let i = 0; items[i] || perInstrument[i]; i++) {
      const withParams = merge(global, perInstrument[i] || {});
      const item = merge(withParams, items[i] || {}) as UniverseItem;
      const def = item.instrumentDefinition;
      if (universeArg !== "()" && def && Object.keys(def).length > 0 && !def[this.instrumentCodeField])
        this.validateUserDefinedParams(Object.keys(def), i);
      universe.push(item);
    }
    return { universe, globalFCRequestParams };
  }

  protected validateUserDefinedParams(keys: string[], index: number): void {
    const required = this.config.requiredUserDefinedParams;
    if (!required.length) return;
    const have = keys.map((k) => k.toLowerCase());
    const missing = required.filter((p) => !have.includes(p.toLowerCase()));
    if (missing.length > 0)
      throw new AnalyticsError(
        `Missing required user defined params for instrument ${index + 1}: ${missing.map((p) => p + ";").join("")}`,
      );
  }

  /** Output's fields (else the Universe's), accessors cut, capitalised, unique, ErrorMessage last, the code first. */
  protected getFields(): string[] {
    const list = text(this.args[ARG.output])?.split(SEP) || text(this.args[ARG.universe])?.split(SEP);
    if (!list) throw new TypeError("Cannot read properties of undefined (reading 'map')"); // LSEG's text, both missing
    const names = Array.from(new Set(list.map((f) => capitalize(f.match(OUTPUT_FIELD)?.[1] ?? f))));
    if (!names.find((f) => f.toLowerCase() === "errormessage")) names.push("ErrorMessage");
    return this.instrumentCodeByDefault ? [capitalize(this.instrumentCodeField), ...names] : names;
  }

  protected getFieldsUniverse(): UniverseItem[] {
    const first = removeLineBreaks(text(this.args[ARG.universe])).split(SEP)[0].trim();
    const subType = this.args[ARG.subType] as string;
    return [{ instrumentType: subType, instrumentDefinition: { [this.instrumentCodeField]: transformInstrument(first, subType) } }];
  }
}

/** LSEG: FxProcessor (78467): IPA's instrumentType is fxcross, the subtype goes to fxCrossType. */
class FxProcessor extends BaseFCProcessor {
  constructor(subType: FcSubType) {
    super(subType, "fxCrossCode");
  }

  protected getFieldsUniverse(): UniverseItem[] {
    const items = super.getFieldsUniverse();
    toFxCross(items[0]);
    return items;
  }

  protected getUniverse() {
    const r = super.getUniverse();
    r.universe = r.universe.map((item) => (toFxCross(item), item));
    return r;
  }
}

function toFxCross(item: UniverseItem): void {
  let type = item.instrumentType;
  if (type === "multileg") type = "fxmultileg";
  item.instrumentType = "fxcross";
  const def = item.instrumentDefinition;
  if (def && type !== "fxcross" && !def.fxCrossType) def.fxCrossType = type;
}

/** LSEG: OptionProcessor (57015). */
class OptionProcessor extends BaseFCProcessor {
  protected getFields(): string[] {
    const fields = super.getFields();
    return this.instrumentCodeByDefault ? ["InstrumentCode", "FxCrossCode", ...fields.slice(1)] : fields;
  }
}

/** LSEG: RepoProcessor (77965). */
class RepoProcessor extends BaseFCProcessor {
  protected getFields(): string[] {
    const fields = super.getFields();
    return this.instrumentCodeByDefault ? ["InstrumentTag", ...fields.slice(1)] : fields;
  }
}

/** LSEG: VoyageServiceProcessor (92077); built for its validation only (ruling P2). */
class VoyageServiceProcessor extends BaseFCProcessor {
  protected getFields(): string[] {
    return super.getFields().slice(1);
  }

  protected getUniverse() {
    const r = super.getUniverse();
    r.universe = [(r.universe[0] as UniverseItem).instrumentDefinition as UniverseItem];
    return r;
  }
}

// LSEG: qpsProcessorsMapFC (23244); the other subtypes use BaseFCProcessor with their config
function makeProcessor(subType: FcSubType): BaseFCProcessor {
  if (FX_SUBTYPES.includes(subType)) return new FxProcessor(subType);
  if (subType === "option") return new OptionProcessor(subType);
  if (subType === "repo") return new RepoProcessor(subType);
  if (subType === VOYAGE) return new VoyageServiceProcessor(subType);
  return new BaseFCProcessor(subType);
}

// LSEG: BaseFCProcessor.transformInstrument, getInstrumentParam, getDefaultInstrumentForType (41170)
function transformInstrument(first: string, subType: string): string {
  return instrumentParam(first) ?? defaultInstrument(first, subType) ?? first;
}

/** `CODE(…)` gives CODE. */
function instrumentParam(s: string): string | undefined {
  for (let t = 0; t < s.length; ) {
    const open = s.indexOf("(", t);
    if (open === -1) return undefined;
    const close = s.indexOf(")", open);
    if (close !== -1 && close - open > 1 && open > 0) return s.substring(0, open);
    t = open + 1;
  }
  return undefined;
}

function defaultInstrument(s: string, subType: string): string | undefined {
  if (!(/^\((.+)\)$/.test(s) || s === "" || s === "getFields")) return undefined;
  if (subType === "bond") return "US10YT=RR";
  if (["fxcross", "fxforward", "fxforwardforward", "fxnondeliverableforward", "fxspot", "fxswap", "multileg"].includes(subType))
    return "USDEUR";
  return undefined;
}

// ---------------------------------------------------------------------------------------------
// LSEG: Merger (73842). Unmerged branches are shared, not copied, as in LSEG.

const NAME_KEY = "name";

export function merge(base: unknown, extend: unknown): unknown {
  if (base == null) return extend;
  if (extend == null) return base;
  if (Array.isArray(base) && Array.isArray(extend)) return mergeArrays(base, extend);
  if (typeof base === "object" && typeof extend === "object") return mergeObjects(base as Obj, extend as Obj);
  return extend;
}

const named = (a: unknown, name: unknown) => typeof a === "object" && (a as Obj)[NAME_KEY] === name; // throws on null, as LSEG

function mergeArrays(base: unknown[], extend: unknown[]): unknown[] {
  const out: unknown[] = [];
  for (const a of base) {
    if (a && (a as Obj)[NAME_KEY]) {
      const twin = extend.find((e) => named(e, (a as Obj)[NAME_KEY]));
      out.push(twin == null ? a : merge(a, twin));
    } else out.push(a);
  }
  for (const a of extend) {
    if (a && (a as Obj)[NAME_KEY]) {
      if (!base.find((e) => named(e, (a as Obj)[NAME_KEY]))) out.push(a);
    } else if (!base.find((e) => e === a)) out.push(a);
  }
  return out;
}

function mergeObjects(base: Obj, extend: Obj): Obj {
  const out: Obj = {};
  for (const [k, v] of Object.entries(base)) out[k] = merge(v, extend[k]);
  for (const [k, v] of Object.entries(extend)) if (base[k] == null) out[k] = v;
  return out;
}
